import { useMemo, useRef, useState, useEffect } from "react";
import { MagnifyingGlass, User, UserPlus, Check, Prohibit } from "@phosphor-icons/react";
import { TEMP_CUSTOMER_OPTION, BLOCK_TIME_OPTION } from "./useAppointmentForm";
import { sortPatientsByName } from "../../../utils/patientSortUtils";
import { phoneMatchesQuery } from "../../../utils/phoneUtils";

/**
 * Type-to-filter patient picker. An empty query shows the full list, so for
 * small clinics it behaves like the old dropdown. Filters by name or phone.
 */
function PatientSearchSelect({
  patients,
  selectedPatientId,
  isTempCustomer,
  isBlockedTime = false,
  tempCustomersEnabled,
  onSelect,
  error,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(!selectedPatientId && !isTempCustomer);
  const containerRef = useRef(null);

  const filtered = useMemo(() => {
    const sorted = sortPatientsByName(patients);
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    // Phone matching goes through phoneMatchesQuery so "+972…" stored values
    // and local "05…" queries compare on the same normalized digits (same
    // matcher as the patients list). Couple patients: match either partner.
    const qDigits = q.replace(/[^0-9]/g, "");
    const phoneQuery = qDigits.length >= 3 ? q : "";
    return sorted.filter((p) => {
      // Same display-name rule as sortPatientsByName: fall back to fullName
      // for legacy/imported docs that lack firstName/lastName.
      const name = (
        `${p.firstName || ""} ${p.lastName || ""}`.trim() || p.fullName || ""
      ).toLowerCase();
      if (name.includes(q)) return true;
      if (!phoneQuery) return false;
      return (
        phoneMatchesQuery(p.phone || p.mobilePhone, phoneQuery) ||
        (p.isCouple === true &&
          (phoneMatchesQuery(p.partner1?.phone, phoneQuery) ||
            phoneMatchesQuery(p.partner2?.phone, phoneQuery)))
      );
    });
  }, [patients, query]);

  // Close the list when tapping outside.
  useEffect(() => {
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const selected = patients.find((p) => p.id === selectedPatientId);

  return (
    <div className="apt-wiz-patient-select" ref={containerRef}>
      <div className={`apt-wiz-search-box ${error ? "has-error" : ""}`}>
        <MagnifyingGlass size={16} className="apt-wiz-search-icon" />
        <input
          type="text"
          value={query}
          placeholder={
            selected
              ? `${selected.firstName} ${selected.lastName}`
              : isBlockedTime
              ? "חסימת זמן פרטי"
              : isTempCustomer
              ? "לקוח מזדמן"
              : "חיפוש מטופל לפי שם או טלפון..."
          }
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          aria-label="חיפוש מטופל"
        />
      </div>

      {open && (
        <div className="apt-wiz-patient-list" role="listbox">
          {/* Special options first — always visible above the patient list */}
          {tempCustomersEnabled && (
            <button
              type="button"
              className={`apt-wiz-patient-row temp ${isTempCustomer ? "selected" : ""}`}
              onClick={() => {
                onSelect(TEMP_CUSTOMER_OPTION);
                setQuery("");
                setOpen(false);
              }}
            >
              <span className="apt-wiz-patient-avatar temp">
                <UserPlus size={14} weight="fill" />
              </span>
              <span className="apt-wiz-patient-name">לקוח מזדמן (ללא תיק)</span>
            </button>
          )}
          {/* Blocked time — always available, no preference flag */}
          <button
            type="button"
            className={`apt-wiz-patient-row block-time ${isBlockedTime ? "selected" : ""}`}
            onClick={() => {
              onSelect(BLOCK_TIME_OPTION);
              setQuery("");
              setOpen(false);
            }}
          >
            <span className="apt-wiz-patient-avatar block-time">
              <Prohibit size={14} weight="fill" />
            </span>
            <span className="apt-wiz-patient-name">חסימת זמן פרטי</span>
          </button>
          {filtered.length === 0 && (
            <div className="apt-wiz-patient-empty">לא נמצאו מטופלים</div>
          )}
          {filtered.map((p) => {
            const isSel = p.id === selectedPatientId;
            return (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={isSel}
                className={`apt-wiz-patient-row ${isSel ? "selected" : ""}`}
                onClick={() => {
                  onSelect(p.id);
                  setQuery("");
                  setOpen(false);
                }}
              >
                <span className="apt-wiz-patient-avatar">
                  <User size={14} weight="fill" />
                </span>
                <span className="apt-wiz-patient-name">
                  {p.firstName} {p.lastName}
                </span>
                {isSel && <Check size={14} weight="bold" className="apt-wiz-patient-check" />}
              </button>
            );
          })}
        </div>
      )}

      {error && <div className="apt-wiz-field-error">{error}</div>}
    </div>
  );
}

export default PatientSearchSelect;
